import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';

import { useHandleInvitations } from '../hooks/useHandleInvitation';

import { ChallengeTotalStats } from './totalStats';

import { Button, Loader, Tags } from '@/components';
import { ArrowDateSelector } from '@/components/GenericsTournaments';
import { Paginator } from '@/components/Paginator';
import { TshirtTeam } from '@/components/TshirtTeam';
import useAuth from '@/hooks/useAuth';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import useToast from '@/hooks/useToast';
import useTourney from '@/hooks/useTourney';
import useTshirtAndShield from '@/hooks/useTshirtAndShield';
import {
  parseParamsTshirtAndShield,
  tshirtKeys,
} from '@/modules/edit-my-account/functions/utils';
import { dateResults, requestCallangeEveryone } from '@/services/challenges';
import { initialTshitAndShield } from '@/store/tshirtAndShield';
import { ChallengeInitResponse, DateResultsResponse } from '@/types/challenges';
import { SvgCamiseta } from '@/types/editProfile';
import { cn, PLAYER_STATUS_COLORS_ID_BY_NAME } from '@/utils';

interface ChallengeItemProps {
  id: number;
  title: string;
  points: number;
  isWinner: boolean;
  isDraw?: boolean;
  teamName: string;
  isLast?: boolean;
  isLoser?: boolean;
  onAction?: () => void;
  isDisabled?: boolean;
  tshirt?: SvgCamiseta;
}

export const ChallengeResults = () => {
  const [resultsData, setResultsData] = useState<ChallengeItemProps[]>([]);
  const [totalPoints, setTotalPoints] = useState<number>(0);
  const [paginatedResults, setPaginatedResults] = useState<
    ChallengeItemProps[]
  >([]);
  const { tourneyData, updateTourney } = useTourney();

  const { userInitialData } = useAuth();

  const initChallenge = tourneyData.tourney as ChallengeInitResponse;

  const toast = useToast();
  const { isDesktop, isMobile } = useIsDesktop();

  const { onHandleSubmit } = useHandleInvitations(resultsData);

  const currentMatchDayId = tourneyData?.matchDay?.id || 0;

  const GDTisOver =
    userInitialData?.veda &&
    userInitialData?.fechaActiva.id ===
      userInitialData?.todasLasFechas[
        userInitialData?.todasLasFechas.length - 1
      ]?.id;

  const {
    data: dataRanking,
    isFetching,
    isLoading,
  } = useQuery(
    ['fechaIdChallenge', currentMatchDayId],
    async () => {
      if (currentMatchDayId !== 0) {
        const response = await dateResults(currentMatchDayId);
        if (response.estado) {
          return response;
        } else {
          toast.show(response.mensaje);
          return null;
        }
      }
      return null;
    },
    {
      refetchOnWindowFocus: false,
      keepPreviousData: false,
      enabled: currentMatchDayId !== 0,
    }
  );

  const handleResultData = useCallback(
    (results: DateResultsResponse) => {
      if (!results || !tourneyData) return;

      setResultsData(
        results.ultimosResultados.map((result) => {
          // Generamos los datos dinámicamente
          const tshirtData = parseParamsTshirtAndShield<SvgCamiseta>(
            result?.camiseta,
            tshirtKeys,
            initialTshitAndShield.tshirt
          );

          return {
            id: result.idUsuario,
            title: `${result.nombres} ${result.apellido}`,
            points: result.puntosTotal,
            isWinner: results.ptos_fecha > result.puntosTotal,
            isDraw: result.puntosTotal === results.ptos_fecha,
            isLoser: results.ptos_fecha < result.puntosTotal,
            teamName: result.nombreEquipoFantasia,
            tshirt: tshirtData,
            isDisabled:
              [
                ...tourneyData.tourneysPending,
                ...tourneyData.tourneysPlaying,
              ].some(
                (challenge) => challenge.idUsuarioRival === result.idUsuario
              ) || GDTisOver,
          };
        })
      );
      setTotalPoints(results.ptos_fecha);
    },
    [tourneyData]
  );

  useEffect(() => {
    if (dataRanking?.estado && (!isLoading || !isFetching)) {
      handleResultData(dataRanking);
    }
  }, [dataRanking, handleResultData, isFetching, isLoading]);

  const handlePageChange = useCallback((currentItems: ChallengeItemProps[]) => {
    setPaginatedResults(currentItems);
  }, []);

  const challangeEveryone = useCallback(async () => {
    const pendingChallenges = resultsData.filter(
      (result) => !result.isDisabled
    );

    if (pendingChallenges.length === 0) {
      toast.show('No hay rivales disponibles para desafiar');
      return;
    }

    try {
      const response = await requestCallangeEveryone(
        tourneyData?.matchDay?.id || 0
      );

      if (!Reflect.has(response, 'mensaje')) {
        if (dataRanking && dataRanking.estado && !isFetching && !isLoading) {
          handleResultData(dataRanking);
          updateTourney({
            idsPendings: [
              ...(tourneyData.idsPendings || []),
              ...pendingChallenges.map((user) => user.id),
            ],
          });
        }
        toast.show('Desafiaste a tus rivales de la fecha pasada.');
      } else {
        toast.show(response.mensaje);
      }
    } catch (error) {
      console.error(error);
      toast.show('Error al desafiar a todos tus rivales');
    }
  }, [
    dataRanking,
    handleResultData,
    isFetching,
    isLoading,
    resultsData,
    toast,
    tourneyData,
    updateTourney,
  ]);

  const showLoader = isLoading || isFetching;

  const disablingMultiInvitations = useMemo(
    () => resultsData.every((result) => result.isDisabled),
    [resultsData]
  );

  return (
    <>
      <Loader isVisible={showLoader} timeout={isFetching} />
      <div className="w-full mt-10">
        {isMobile && initChallenge.hist_jugados && (
          <ChallengeTotalStats init={tourneyData.tourney} />
        )}
        <ArrowDateSelector
          key={initChallenge.fechas.length}
          disabled={isFetching || isLoading}
          matchsDaysAvailables={initChallenge.fechas}
        />
        <TeamStats resultsData={resultsData} totalPoints={totalPoints} />

        {resultsData.length > 1 && (
          <div
            key={tourneyData?.matchDay?.id}
            className="bg-gdt-gray-7 pt-3 pb-4 px-8 flex justify-end"
          >
            <Button
              className="no-underline font-bold"
              disabled={disablingMultiInvitations}
              type="button"
              variant="text"
              onClick={() => !disablingMultiInvitations && challangeEveryone()}
            >
              Volver a desafiar a todos
            </Button>
          </div>
        )}
        {resultsData.length > 0 ? (
          (isDesktop ? paginatedResults : resultsData).map((result) => (
            <ChallengeItem
              key={result.id}
              id={result.id}
              isDisabled={result.isDisabled}
              isDraw={result.isDraw}
              isLast={
                result.id ===
                (isDesktop
                  ? paginatedResults[paginatedResults.length - 1].id
                  : resultsData[resultsData.length - 1].id)
              }
              isLoser={result.isLoser}
              isWinner={result.isWinner}
              points={result.points}
              teamName={result.teamName}
              title={result.title}
              tshirt={result.tshirt}
              onAction={() => onHandleSubmit({ id: result.id })}
            />
          ))
        ) : (
          <div className="text-center text-gdt-gray-4 py-6">
            No jugaste desafíos en esta fecha
          </div>
        )}
        <div
          className={cn('md:bg-white', {
            hidden: resultsData.length <= 4 || !isDesktop,
          })}
        >
          <Paginator
            key={tourneyData.matchDay?.id}
            currentContentPage={handlePageChange}
            items={resultsData}
            itemsPerPage={5}
            marginPagesDisplayed={1}
            pageRangeDisplayed={3}
          />
        </div>
      </div>
    </>
  );
};

const StatsItem = ({
  title,
  value,
}: {
  title: string;
  value: string | number;
}) => (
  <div className="flex items-center gap-2 flex-col md:flex-row ">
    <span className="text-xs font-semibold text-gdt-gray-4">{title}</span>
    <strong className="text-sm text-gdt-gray-2">{value}</strong>
  </div>
);

const formatNumberToDash = (number?: string | number) => {
  if (number === undefined || number === null || number === 0) {
    return '-';
  }
  return number;
};

const ChallengeItem = ({
  title,
  points,
  isWinner,
  teamName,
  isLast,
  onAction,
  isDraw = false,
  isLoser,
  isDisabled,
  tshirt,
  id,
}: ChallengeItemProps) => (
  <div
    className={cn('grid grid-cols-2 bg-white py-4 px-6', {
      'border-b border-gdt-gray-5': !isLast,
    })}
  >
    <div className="flex  items-center  gap-x-3">
      <div className="rounded-full bg-gdt-blue-light2 text-gdt-blue-header px-3 py-1 font-bold self-start">
        <span>VS</span>
      </div>
      <figure className="w-10 min-w-10 h-11 self-start hidden md:block">
        <TshirtTeam id={id} tshirt={tshirt} />
      </figure>
      <div className="flex flex-col">
        <span className="text-sm font-bold">{title}</span>
        <span className="text-xs text-gdt-gray-2 font-semibold">
          {teamName}
        </span>
        <span className="text-sm text-gdt-blue-header font-bold">
          {points} puntos
        </span>
      </div>
    </div>
    <div className="flex flex-col md:flex-row justify-between items-end md:items-center">
      <div>
        {isDraw && (
          <Tags.Alert statusId={PLAYER_STATUS_COLORS_ID_BY_NAME.YELLOW}>
            EMPATASTE
          </Tags.Alert>
        )}{' '}
        {isWinner && (
          <Tags.Alert statusId={PLAYER_STATUS_COLORS_ID_BY_NAME.GREEN}>
            GANASTE
          </Tags.Alert>
        )}{' '}
        {isLoser && (
          <Tags.Alert statusId={PLAYER_STATUS_COLORS_ID_BY_NAME.RED}>
            PERDISTE
          </Tags.Alert>
        )}
      </div>
      <Button
        className="border-b border-gdt-blue no-underline font-bold"
        disabled={isDisabled}
        type="button"
        variant="text"
        onClick={onAction}
      >
        Volver a desafiar
      </Button>
    </div>
  </div>
);

const TeamStats = ({
  totalPoints,
  resultsData,
}: {
  totalPoints: number;
  resultsData: ChallengeItemProps[];
}) => {
  const { isDesktop } = useIsDesktop();
  const { userInitialData } = useAuth();

  const { data } = useTshirtAndShield();

  if (!isDesktop) {
    return (
      <div className="flex flex-col bg-white  py-7 gap-5 mx-6 rounded-lg">
        <div className="flex justify-between px-6 border-b border-gdt-gray-5 pb-3">
          <div className="flex gap-x-3">
            <figure className="max-w-10">
              <TshirtTeam
                withShield
                shield={data?.shield}
                tshirt={data?.tshirt}
              />
            </figure>
            <div className="flex flex-col">
              <span className="text-lg font-bold">
                {userInitialData?.nombre_dt}{' '}
              </span>
              <span className="text-xs font-semibold text-gdt-gray-3">
                {userInitialData?.nombre_equipo}
              </span>
            </div>
          </div>
          <h4 className="text-sm font-bold text-nowrap flex flex-col">
            TUS PUNTOS{' '}
            <strong className="text-gdt-blue text-xl border-r pr-5">
              {formatNumberToDash(totalPoints)}
            </strong>
          </h4>
        </div>
        <div className="flex justify-between w-full px-4">
          <StatsItem
            title="JUGADOS"
            value={formatNumberToDash(resultsData.length)}
          />
          <StatsItem
            title="GANADOS"
            value={formatNumberToDash(
              resultsData.filter((result) => result.isWinner).length
            )}
          />
          <StatsItem
            title="EMPATADOS"
            value={formatNumberToDash(
              resultsData.filter((result) => result.isDraw).length
            )}
          />
          <StatsItem
            title="PERDIDOS"
            value={formatNumberToDash(
              resultsData.filter((result) => result.isLoser).length
            )}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex bg-white px-6 py-7 gap-5">
      <h4 className="text-sm font-bold text-nowrap">
        TUS PUNTOS{' '}
        <strong className="text-gdt-blue-dark text-lg border-r pr-5">
          {formatNumberToDash(totalPoints)}
        </strong>
      </h4>
      <div className="flex justify-between w-full">
        <StatsItem
          title="JUGADOS"
          value={formatNumberToDash(resultsData.length)}
        />
        <StatsItem
          title="GANADOS"
          value={formatNumberToDash(
            resultsData.filter((result) => result.isWinner).length
          )}
        />
        <StatsItem
          title="EMPATADOS"
          value={formatNumberToDash(
            resultsData.filter((result) => result.isDraw).length
          )}
        />
        <StatsItem
          title="PERDIDOS"
          value={formatNumberToDash(
            resultsData.filter((result) => result.isLoser).length
          )}
        />
      </div>
    </div>
  );
};
